import React, { useState } from 'react';
import { Button, CircularProgress } from '@mui/material';
import { Download } from '@mui/icons-material';
import { useLanguage } from '../../i18n/LanguageContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

interface ExportButtonProps {
  fetchWithAuth: (url: string, options?: RequestInit) => Promise<Response>;
}

const ExportButton: React.FC<ExportButtonProps> = ({ fetchWithAuth }) => {
  const { t } = useLanguage();
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    try {
      setIsExporting(true);
      
      const response = await fetchWithAuth(`${API_URL}/export/excel`);
      
      if (!response.ok) {
        throw new Error('Failed to export data');
      }

      // Get the blob from the response
      const blob = await response.blob();
      
      // Create a download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers or use default
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'gym-tracker-data.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Button
      variant="outlined"
      startIcon={isExporting ? <CircularProgress size={20} /> : <Download />}
      onClick={handleExport}
      disabled={isExporting}
      sx={{ mb: 2 }}
    >
      {isExporting ? t('common.exporting') : t('common.exportData')}
    </Button>
  );
};

export { ExportButton }; 